import { Component, Input, signal, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { GoogleDriveFile } from '../../models/googleDriveFile';
import { Edit } from '../../models/campaignFile';
import { FileService } from '../../services/file.service';

@Component({
  selector: 'app-edit-recommendations',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './edit-recommendations.component.html',
  styleUrls: ['./edit-recommendations.component.css'],
})
export class EditRecommendationsComponent implements OnInit, OnChanges {
  @Input({ required: true }) file: GoogleDriveFile | null = null;
  @Input({ required: true }) campaignId: string | null = null;

  edits: Edit[] = [];
  isLoading: boolean = false;
  analysisStarted: boolean = false;
  isSubmitting: boolean = false;
  analysisError: string | null = null;

  constructor(private sanitizer: DomSanitizer, private fileService: FileService) {}

  ngOnInit(): void {
    this.loadFileEdits();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['file'] && changes['file'].currentValue !== changes['file'].previousValue) {
      // Reset state when file changes
      this.analysisStarted = false;
      this.isSubmitting = false;
      this.analysisError = null;
      this.loadFileEdits();
    }
  }

  private loadFileEdits(): void {
    if (this.file && this.file.id) {
      this.isLoading = true;
      this.fileService.GetFileEdits(this.file.id).subscribe({
        next: (edits) => {
          this.edits = edits;
          this.isLoading = false;
          if (edits.length === 0) {
            this.loadAnalysisStatus();
          }
        },
        error: (err) => {
          console.error('Failed to load file edits:', err);
          this.edits = [];
          this.isLoading = false;
        }
      });
    } else {
      // Clear edits if no file is selected
      this.edits = [];
      this.isLoading = false;
    }
  }

  private loadAnalysisStatus(): void {
    if (!this.file?.id || !this.campaignId) return;
    const fileId = this.file.id;
    this.fileService.GetAnalyzeContractStatus(this.campaignId, fileId).subscribe({
      next: (status) => {
        // Ignore a late response for a previously selected file
        if (this.file?.id === fileId && status.analyzed) {
          this.analysisStarted = true;
        }
      },
      error: (err) => console.error('Failed to load analysis status:', err)
    });
  }

  // track which cards are open
  openIds = signal<Set<number>>(new Set());

  toggle(i: number) {
    const set = new Set(this.openIds());
    set.has(i) ? set.delete(i) : set.add(i);
    this.openIds.set(set);
  }

  isOpen(i: number) {
    return this.openIds().has(i);
  }


  // Cache SafeHtml per source string. Returning a new SafeHtml object on every change
  // detection pass makes Angular re-assign innerHTML, which destroys any text selection.
  private trustedHtmlCache = new Map<string, SafeHtml>();

  trustHtml(html: string): SafeHtml {
    // Only use on HTML you generate/clean yourself
    const key = html ?? '';
    let safe = this.trustedHtmlCache.get(key);
    if (!safe) {
      safe = this.sanitizer.bypassSecurityTrustHtml(key);
      this.trustedHtmlCache.set(key, safe);
    }
    return safe;
  }

  analyzeContract() {
    if (this.file && this.file.id && this.campaignId && !this.isSubmitting && !this.analysisStarted) {
      this.isSubmitting = true;
      this.analysisError = null;
      this.fileService.AnalyzeContract(this.campaignId, this.file.id).subscribe({
        next: () => {
          this.analysisStarted = true;
          this.isSubmitting = false;
        },
        error: (err) => {
          this.isSubmitting = false;
          if (err?.status === 409) {
            // Already analyzed / in progress (e.g. started from another tab or by another user)
            this.analysisStarted = true;
            return;
          }
          console.error('Failed to start contract analysis:', err);
          this.analysisError = 'Could not start the contract analysis. Please try again.';
        }
      });
    }
  }
}
